import {useCallback, useEffect} from 'react';
import {
  useIVStore,
  DEFAULT_IMAGE_DETAILS,
  type IdentityVerificationSchema,
} from 'store/identityVerification/identityVerification';
import {STORAGE_KEYS} from 'utils/constants';
import {useChekinSDK} from 'context/ChekinSDK';
import {IV_DOCUMENT_IMAGE_TYPES, IVIntent} from '@guestapp/sdk';
import {useIsRemoteIV} from 'hooks/iv/useRemoteStartVerificationLink';
import useBuildOCRDetectedData from 'hooks/useBuildOCRDetectedData';
import {SessionStorage} from '@guestapp/core';
import {getFeatureFlags} from 'utils/features';

export const setIVIntentRemoteSession = (intentId: string) => {
  SessionStorage.set(STORAGE_KEYS.ivIntentRemoteSession, intentId);
};

export const getIVIntentRemoteSession = (): string | null =>
  getFeatureFlags('IVCO_49_MOVE_IV_TO_MOBILE')
    ? SessionStorage.get(STORAGE_KEYS.ivIntentRemoteSession)
    : null;

export const removeIVIntentRemoteSession = () =>
  SessionStorage.remove(STORAGE_KEYS.ivIntentRemoteSession);

export const convertImagesDetailsToStoreState = (
  imagesDetails?: IVIntent['image_details'],
) => {
  if (!imagesDetails) {
    return {imagesDetails: DEFAULT_IMAGE_DETAILS};
  }

  Object.values(IV_DOCUMENT_IMAGE_TYPES).reduce((acc, key) => {
    const imageDetails = imagesDetails[key] || DEFAULT_IMAGE_DETAILS[key];

    acc[key] = {
      ...imageDetails,
      uploaded: Boolean(imageDetails.image),
    };
    return acc;
  }, {} as Required<IVIntent>['image_details']);

  return {
    imagesDetails: {
      ...DEFAULT_IMAGE_DETAILS,
      ...imagesDetails,
    },
  };
};

const convertIvIntentToStoreState = (
  ivIntentItem: IVIntent,
): Partial<IdentityVerificationSchema> => {
  return {
    setupFormDate: {
      nationality: ivIntentItem.nationality,
      documentType: ivIntentItem.type_doc,
    },
    ocrResultData: {
      ocrPassed: Boolean(ivIntentItem.document_passed),
    },
    biomatchResultData: {
      biomatchPassed: Boolean(ivIntentItem.biomatch_passed),
      distance: null,
    },
    ...convertImagesDetailsToStoreState(ivIntentItem.image_details),
  };
};

export const useIVRemoteSync = () => {
  const {buildFormData} = useBuildOCRDetectedData();
  const {intentId: urlIntentId} = useIsRemoteIV();
  const {getIvIntent, startIVOnMobile} = useChekinSDK();
  const {ivIntentId: storeIvIntentId, sync, setRemoteSession} = useIVStore();
  const synchronized = Boolean(storeIvIntentId);

  const loadRemoteState = useCallback(
    async ({postHandler}: {postHandler?: () => void}) => {
      const intentId = getIVIntentRemoteSession();
      if (intentId) {
        const data = await getIvIntent(intentId);
        sync(convertIvIntentToStoreState(data));

        if (data?.mrz) {
          const builtFormData = buildFormData(data.mrz);
          SessionStorage.set(STORAGE_KEYS.personalFormData, builtFormData);
        }

        postHandler?.();
      }
    },
    [buildFormData, getIvIntent, sync],
  );

  useEffect(() => {
    if (urlIntentId && getFeatureFlags('IVCO_49_MOVE_IV_TO_MOBILE')) {
      setRemoteSession(true);
      setIVIntentRemoteSession(urlIntentId);
      startIVOnMobile(urlIntentId);
    }
  }, [urlIntentId, setRemoteSession, startIVOnMobile]);

  useEffect(() => {
    const asyncIVIntentRemoteSession = async () => {
      const intentId = getIVIntentRemoteSession();
      if (intentId) {
        const data = await getIvIntent(intentId || '');
        sync(convertIvIntentToStoreState(data));
      }
    };

    if (getFeatureFlags('IVCO_49_MOVE_IV_TO_MOBILE')) {
      asyncIVIntentRemoteSession();
    }
  }, [getIvIntent, sync]);

  return {
    synchronized,
    loadRemoteState,
  };
};
